import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {DataService} from '../../../Services/data.service';
import {EmailModel} from '../../../DataModels/email-model';
import {BsToastService} from '../../../Services/bs-toast-service';
import {Modal} from 'bootstrap';
import {SendEmailModalComponent} from '../send-email-modal/send-email-modal.component';

@Component({
  selector: 'app-activity-feed-modal',
  templateUrl: './activity-feed-modal.component.html',
  styleUrls: ['./activity-feed-modal.component.scss'],
  standalone: false
})
export class ActivityFeedModalComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('closeButton') closeButton: ElementRef;
  // Reference the embedded Send Email modal component's host element
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private emailModalHost: ElementRef;
  searchText: string;

  @Input() src: any;
  // Optional document number to display in headers/attachments when available
  @Input() docNumber?: any;

  activityFeedList: any;
  activityFeedCount = 0;

  showFeed = false;

  // Email modal binding object for this modal
  emailObject: EmailModel = new EmailModel();

  constructor(private table: DataService, private notification: BsToastService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    // this.loadAuditTrail();
  }

  private _showListener: (() => void) | null = null;

  ngAfterViewInit() {
    const el = document.getElementById('activityFeedModal');
    if (el) {
      this._showListener = () => {
        this.loadActivityFeed();
        this.cdr.detectChanges();
      };
      el.addEventListener('show.bs.modal', this._showListener);
    }
  }

  ngOnChanges() {
    this.loadActivityFeed();
  }

  loadActivityFeed() {

    this.activityFeedList = [];
    this.activityFeedCount = 0;

    if (this.src) {
      // console.log('src: ' + JSON.stringify(this.src));
      this.src.forEach((rec) => {
          this.activityFeedList.push(rec);
      });
      this.activityFeedCount = this.activityFeedList.length;
    }
  }

  onPrint() {
    try {
      const html = this.buildPrintHtml();
      this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Activity Feed print failed', e);
      this.notification.showErrorToast('Unable to print Activity Feed');
    }
  }

  private async ensureActivityDataReady(): Promise<void> {
    // If list already exists (even empty array), consider it ready
    if (this.activityFeedList !== undefined) {
      return;
    }
    // If src is provided and list not yet built, build it now
    if (this.src && Array.isArray(this.src)) {
      this.activityFeedList = [];
      this.src.forEach((rec: any) => this.activityFeedList.push(rec));
      this.activityFeedCount = this.activityFeedList.length;
      return;
    }
    // Last resort: yield to let bindings stabilize
    await Promise.resolve();
  }

  async onEmail() {
    try {
      // Ensure data is ready before generating HTML to attach
      await this.ensureActivityDataReady();
      const html = this.buildPrintHtml(true);

      // Build EmailModel for Send Email modal using known-good pattern
      const emailDocObj = {} as EmailModel;
      const emailDocumentName = 'Activity Feed';
      emailDocObj.print_orientation = '1'; // portrait by convention;
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = null as any;
      emailDocObj.toEmail = '';
      emailDocObj.body = 'Please see the attached ' + emailDocumentName + '.<br><br><br>'
        + 'Best regards,'
        + '<br><br>'
        + 'AcctXErp'
        + '<br><br>';
      emailDocObj.Subject = 'AcctXErp ' + emailDocumentName;
      emailDocObj.activity_message = emailDocumentName;

      try {
        emailDocObj.pdfHtml = html || '';
      } catch {
        emailDocObj.pdfHtml = '';
      }

      const safeName = (emailDocumentName || 'document').replace(/\s+/g, '_');
      emailDocObj.FileName = `${safeName}.pdf`;

      // Bind to Send Email modal input and flush bindings
      this.emailObject = emailDocObj;
      this.cdr.detectChanges();

      // Prefer to locate the Send Email modal element via the directive (component instance)
      let emailEl: HTMLElement | null = null;
      try {
        emailEl = (this.emailModalHost?.nativeElement as HTMLElement)?.querySelector?.('#sendEmailModal') || null;
      } catch {
        emailEl = null;
      }
      // Fallback to global lookup if not found via directive
      if (!emailEl) {
        emailEl = document.getElementById('sendEmailModal');
      }

      // If the modal element is rendered under the Activity Feed modal, move it to body so it overlays properly
      if (emailEl && emailEl.closest('#activityFeedModal')) {
        document.body.appendChild(emailEl);
      }

      // Open the Send Email modal programmatically (no data-bs-toggle)
      const modal = emailEl ? Modal.getOrCreateInstance(emailEl) : null;
      modal?.show();
    } catch (e) {
      console.error('Activity Feed prepare email failed', e);
      this.notification.showErrorToast('Unable to prepare email for Activity Feed');
    }
  }

  private printHtmlInIframe(html: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      this.notification.showErrorToast('Unable to access print frame');
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    const printAndCleanup = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 1000);
      }
    };

    if (doc.readyState === 'complete') {
      setTimeout(printAndCleanup, 250);
    } else {
      iframe.onload = () => setTimeout(printAndCleanup, 250);
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const list: any[] = Array.isArray(this.activityFeedList) ? this.activityFeedList : [];
    const rows = list.map((af: any) => {
      const dt = af?.ActivityDate ? new Date(af.ActivityDate) : null;
      const dtStr = dt ? dt.toLocaleString() : '';
      const user = af?.user_name || '';
      const msg = af?.message || '';
      return `<tr><td>${dtStr}</td><td>${this.escapeHtml(user)}</td><td>${this.escapeHtml(msg)}</td></tr>`;
    }).join('');

    const styles = `
      <style>
        @page { size: landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        body { font-family: Arial, Helvetica, sans-serif; margin: 24px; }
        h1 { font-size: 18px; margin: 0 0 8px 0; }
        .meta { font-size: 12px; color: #555; margin-bottom: 8px; }
        .meta span { display: inline-block; margin-right: 16px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        th { background: #f6f6f6; text-align: left; }
      </style>
    `;

    const title = 'Activity Feed';
    const runAt = new Date().toLocaleString();
    const transactionNumber = (this.docNumber != null && this.docNumber !== undefined && this.docNumber !== '')
      ? this.docNumber
      : ((this as any)?.vm?.item?.header?.number || '');

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          ${styles}
        </head>
        <body>
          <h1>${title}</h1>
          <div class="meta"><span><strong>Run:</strong> ${this.escapeHtml(runAt)}</span><span><strong>Transaction #:</strong> ${this.escapeHtml(String(transactionNumber))}</span><span><strong>Count:</strong> ${this.activityFeedCount || 0}</span></div>
          <table>
            <thead>
              <tr><th>Date Time</th><th>User Name</th><th>Activity</th></tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          <div class="no-print" style="margin-top:16px"><button onclick="window.print()">Print</button></div>
        </body>
      </html>
    `;
  }

  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  ngOnDestroy() {
    const el = document.getElementById('activityFeedModal');
    if (el && this._showListener) {
      el.removeEventListener('show.bs.modal', this._showListener);
    }
    const modalEl = this.closeButton?.nativeElement?.closest('.modal');
    if (modalEl) {
      const modal = Modal.getInstance(modalEl);
      if (modal) {
        modal.hide();
      }
    }
  }
}
